"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
const CorporatePackageForm = dynamic(() => import("@/components/forms/corporate-package-form").then(mod => mod.CorporatePackageForm), {
  ssr: false
});
import { Section } from "@/components/section";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BorderBeam } from "@/components/ui/border-beam";
import { HexagonPattern } from "@/components/ui/hexagon-pattern";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { Check, Star, Zap, Users, Building2, Crown } from "lucide-react";
import { MouseEventHandler, Suspense, useState } from "react";
import { cn } from "@/lib/utils";
const list = [{
  name: "1 License = 8 Hours of Training",
  description: ["Each license corresponds to a full day of training, which is 8 hours."]
}, {
  name: "Half License = 4 Hours of Training",
  description: ["If a session is 4 hours, it will correspond to half a license."]
}, {
  name: "Training Day Definition:",
  description: ["A training day is defined as 8 hours of instructor-led training.", "For technical programs or shorter sessions, such as 4-hour sessions, the trainer will still charge for a full 8-hour day of training, regardless of the shorter duration."]
}];
const packages = [{
  id: "starter",
  name: "Starter",
  description: "Perfect for small teams",
  licenses: "120",
  unit: "licenses",
  icon: Zap,
  features: ["64 hours of Group Training", "Up to 10 training sessions", "2000+ Training programs"],
  buttonText: "Get Started",
  popular: false
}, {
  id: "growth",
  name: "Growth",
  description: "For growing organizations",
  licenses: "320",
  unit: "licenses",
  icon: Users,
  features: ["160 hours of Group Training", "Up to 25 training sessions", "2000+ Training programs"],
  buttonText: "Get Started",
  popular: true
}, {
  id: "enterprise",
  name: "Enterprise",
  description: "For large organizations",
  licenses: "800",
  unit: "licenses",
  icon: Building2,
  features: ["400 hours of Group Training", "Up to 60 training sessions", "2000+ Training programs"],
  buttonText: "Get Started",
  popular: false
}, {
  id: "custom",
  name: "Custom",
  description: "Tailored to your needs",
  licenses: "∞",
  unit: "unlimited",
  icon: Crown,
  features: ["Unlimited Training Hours", "Unlimited Training Sessions", "Custom Training Programs"],
  buttonText: "Contact Sales",
  popular: false
}];
const CorporateTrainingPackages = () => {
  const $ = _c(12);
  const [open, setOpen] = useState(false);
  const [pack, setPackage] = useState("starter");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = event => {
      setPackage(event.currentTarget.dataset.package);
      setOpen(true);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleOpen = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HexagonPattern radius={30} x={-1} y={-1} className={cn("mask-[linear-gradient(to_top_right,transparent,transparent,white)]")} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="pt-8"><div className="grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">{packages.map(pkg => {
          const Icon = pkg.icon;
          return <div key={pkg.id} className={cn("group/pc relative flex flex-col transition-all duration-300", pkg.popular ? "z-10 lg:scale-105" : "")}><div className={cn("absolute inset-0 rounded-3xl opacity-10 blur-xl transition-all duration-500 group-hover/pc:opacity-30", pkg.popular ? "bg-secondary opacity-20 blur-xl group-hover/pc:opacity-40" : "bg-secondary/10 dark:bg-white/5")} /><Card className={cn("relative flex h-full flex-col justify-between gap-0 overflow-visible rounded-3xl border p-8 shadow-xl ring-transparent backdrop-blur-xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl", "border-slate-200/80 bg-white/80 text-slate-900 hover:border-slate-300/80", "dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:border-white/20")}>{pkg.popular && <Badge variant="secondary" className="bg-secondary text-secondary-foreground shadow-secondary/35 hover:bg-secondary absolute -top-3.5 left-1/2 z-20 flex -translate-x-1/2 cursor-default items-center gap-1.5 rounded-full border-0 px-3 py-1 text-[10px] font-bold tracking-wider uppercase shadow-lg"><Star className="text-secondary-foreground h-3 w-3 fill-current" />Most Popular</Badge>}{pkg.popular && <><BorderBeam duration={6} size={150} borderWidth={1.5} className="via-secondary from-transparent to-transparent" /><BorderBeam duration={6} delay={3} size={150} borderWidth={1.5} className="via-secondary from-transparent to-transparent" /></>}<CardHeader className="mb-6 flex flex-col items-start gap-1 p-0"><div className={cn("mb-4 inline-flex items-center justify-center rounded-2xl p-3 transition-all duration-300", pkg.popular ? "bg-secondary/20 text-secondary scale-110 shadow-inner" : "bg-primary/5 text-primary dark:text-secondary dark:bg-white/10")}><Icon className="h-6 w-6" /></div><CardTitle className="text-2xl leading-none font-bold text-slate-900 dark:text-white">{pkg.name}</CardTitle><CardDescription className="mt-1 text-sm text-slate-500 dark:text-white/70">{pkg.description}</CardDescription><div className="mt-6 flex items-baseline"><span className="text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white">{pkg.licenses}</span><span className="ml-2 text-sm font-medium text-slate-500 dark:text-white/60">{pkg.unit}</span></div></CardHeader><CardContent className="flex flex-1 flex-col p-0"><ul className="mb-0 flex-1 space-y-4">{pkg.features.map(_temp)}</ul></CardContent><div className="mt-8"><Button onClick={handleOpen} data-package={pkg.id} variant={pkg.popular ? "secondary" : "outline"} size="lg" className={cn("flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl text-center font-semibold transition-all duration-300", pkg.popular ? "bg-secondary text-secondary-foreground shadow-secondary/25 shadow-lg hover:scale-[1.02]" : "border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 hover:text-slate-900 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:border-white/25 dark:hover:bg-white/15 dark:hover:text-white")}>{pkg.buttonText}</Button></div></Card></div>;
        })}</div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="col-span-12 flex flex-col justify-center rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-xl backdrop-blur-md lg:col-span-7 dark:border-white/10 dark:bg-white/5"><ol className="space-y-6">{list.map(_temp3)}</ol></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CardHeader className="mb-4 p-0"><Heading as="h6" className="flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white"><span className="bg-secondary h-2.5 w-2.5 shrink-0 animate-ping rounded-full" />What is not included?</Heading></CardHeader>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mt-12 grid grid-cols-12 items-stretch gap-6">{t3}<div className="col-span-12 flex lg:col-span-5"><Card className="flex w-full flex-col justify-center gap-0 rounded-3xl border border-slate-200 bg-white/80 p-8 text-slate-900 shadow-xl ring-transparent backdrop-blur-md dark:border-white/10 dark:bg-white/5 dark:text-white">{t4}<CardContent className="p-0"><Text className="text-sm leading-relaxed text-slate-600 dark:text-white/80">Certification/exam fees, post-training support, and labs are not included in the pricing and will be billed separately. The pricing for onsite training does not cover facilities and catering services. Certain customized and advanced training sessions may incur additional charges.</Text></CardContent></Card></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = pack as string;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setOpen(false);
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t6) {
    t8 = <DialogContent className="p-0 sm:max-w-xl"><Suspense><CorporatePackageForm packageName={t6} onSuccess={t7} /></Suspense></DialogContent>;
    $[7] = t6;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== open || $[10] !== t8) {
    t9 = <Section className="corporate-training-packages relative overflow-hidden" id="corporate-training-packages" title="Customized Instructor-led Corporate Training Prices and Packages for Organizations Globally">{t1}{t2}{t5}<Dialog open={open} onOpenChange={setOpen}>{t8}</Dialog></Section>;
    $[9] = open;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
};
export default CorporateTrainingPackages;
function _temp(feature, idx) {
  return <li key={idx} className="flex items-start text-sm leading-relaxed text-slate-700 dark:text-white/95"><Check className="text-secondary mt-0.5 mr-3 h-5 w-5 shrink-0" /><span>{feature}</span></li>;
}
function _temp2(desc, descIndex) {
  return <li key={descIndex} className="before:bg-secondary/70 relative pl-4 leading-relaxed before:absolute before:top-2 before:left-0 before:h-1.5 before:w-1.5 before:rounded-full">{desc}</li>;
}
function _temp3(item, index) {
  return <li key={index} className="space-y-2"><span className="flex items-center gap-3 text-lg font-semibold text-slate-900 dark:text-white"><span className="border-secondary/30 bg-secondary/20 text-secondary flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-bold">{index + 1}</span>{item.name}</span><ul className="space-y-2 pl-10 text-sm text-slate-600 dark:text-white/80">{item.description.map(_temp2)}</ul></li>;
}